import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { ForgeButton, IconButton } from '@/components/forge/ForgeButton'
import { ForgeDialog } from '@/components/forge/ForgeDialog'
/* `ForgeSelect` left with the one-at-a-time live picker. Live is a grid of every
   pinned sensor now, so there is nothing to choose between. */
import { Icon } from '@/components/forge/Icon'
import { Panel } from '@/components/forge/Panel'
import { Segmented, SegmentedItem } from '@/components/forge/Chip'
import { PlainBadge, StateBadge } from '@/components/forge/StatusBadge'
import { EmptyState, ErrorState, SkeletonRows } from '@/components/forge/States'
import { Eyebrow, Mono, Num } from '@/components/forge/primitives'
import { usePermission } from '@/features/auth/permissions'
import { MAX_PINNED_SENSORS, anomalyAssetScope, symptomSensorNames } from '@/features/anomalies/api'
import {
  useLiveSensorTagReadings,
  useSaveAnomalySensorTags,
  useSensorTagReadings,
} from '@/features/anomalies/queries'
import { useSensors } from '@/features/sensors/queries'
import { downloadCsv, toCsv } from '@/lib/csv'
import { formatDateTime } from '@/lib/format'
import { useToast } from '@/providers/ToastProvider'
import { cn } from '@/lib/cn'
import { TraceChart, TraceSkeleton } from './TraceChart'
import { IST_DATE, IST_STAMP, IST_TIME, sameDayInZone } from './time'

/**
 * Analysis · Timeseries — the pinned sensors, two ways.
 *
 * ── Real, where this was two hardcoded traces ────────────────────────────
 * The demo drew two sensors from literal arrays, with a live badge, a per-card
 * range control and an "Add sensor" button, none of which had an endpoint. All
 * four exist now, off three routes:
 *
 *   `sensor_tags` on the detail read            which sensors are on the board
 *   `GET  .../sensor-tags/{tag}/readings`       the anomaly window, paged
 *   `GET  .../sensor-tags/{tag}/stream`         the live feed
 *   `PUT  .../sensor-tags`                      pinning, as a full-set replace
 *
 * ── The two modes are different SUBJECTS, not two ranges of one ──────────
 * History reads `[window_start, window_end]` — the event, closed and immutable,
 * and the only range that endpoint will serve: `from`/`to` outside it are a 400
 * rather than a clamp. Live reads the stream, which pushes readings as the
 * broker delivers them and has no relationship to the window at all. A sensor
 * can be perfectly quiet live while its window holds the spike that raised the
 * anomaly, so the toggle says which one is on screen rather than implying they
 * are the same trace at two zoom levels.
 *
 * ── Why the duration chips differ between them ──────────────────────────
 * In history they are ZOOM levels inside the window, built from the window's own
 * length — a 75-second event is not offered a five-minute chip, because there is
 * no five minutes to show and no endpoint that could fetch it. In live they are
 * how much of the feed to KEEP on screen. The prototype's 1H/6H/24H/7D/30D row
 * is not here in either mode: `readings_between` has exactly one caller and it
 * is bounded by the anomaly window, so nothing in this API can answer for an
 * hour ago. It is in BACKEND-GAPS.md.
 *
 * ── One live card at a time, by design and not by accident ──────────────
 * `try_acquire(user.id, "anomaly_sensor_tags", 4)` caps concurrent streams per
 * user and refuses the fifth with a `ValidationAppError`. Live mode therefore
 * shows ONE chart with a sensor picker above it rather than a live grid: the cap
 * is then unreachable, and nobody has to be told about a limit at the moment
 * they hit it.
 */

/**
 * The zoom levels history offers, longest first.
 *
 * Filtered against the window's own length at use. The full window is always the
 * first chip and is always selected first — the event is the subject, and a
 * chart that opened on a slice of it would hide the part that is not in view.
 */
const HISTORY_SPANS = [
  { key: '60s', ms: 60_000 },
  { key: '30s', ms: 30_000 },
  { key: '10s', ms: 10_000 },
  { key: '5s', ms: 5_000 },
]

/** How much of the live feed to keep. The server backfills 60s on connect. */
const LIVE_SPANS = [
  { key: '30s', ms: 30_000 },
  { key: '2m', ms: 120_000 },
  { key: '5m', ms: 300_000 },
  { key: '15m', ms: 900_000 },
]

const DEFAULT_LIVE_SPAN = 120_000

export function TimeseriesView({ anomaly }) {
  const { t } = useTranslation(['app', 'common'])
  const [mode, setMode] = useState('history')
  const [adding, setAdding] = useState(false)
  const [expanded, setExpanded] = useState(null)
  const save = useSaveAnomalySensorTags()

  const tags = anomaly?.sensorTags ?? []
  /**
   * ── `type` is membership's second axis, not its first ──────────────────
   * Every tag here is ATTACHED to the anomaly. `pinned` means it is also
   * charted; `unpinned` means it is attached and not charted. A tag that is
   * neither is not in this list at all — its row was never written, or was
   * removed.
   *
   * The engine writes both kinds: it pins what it read and unpins what it
   * considered and set aside, which is why the second list is not simply "things
   * the user added and has not got round to".
   */
  /**
   * ── The alert's own sensors pin and unpin like any other ───────────────
   * `isSource` tags are the ones the engine attached because they raised the
   * alert. They used to be charted whatever their `type`, with no unpin
   * control, which left a reader no way to clear one off a crowded board.
   * `type` alone decides charting now, for every tag. What a source tag still
   * cannot do is be REMOVED — `verify_sensor_tags` 400s on that — so unpinning
   * one moves it to the attached list with a Pin button and no remove.
   */
  const pinned = tags.filter((tag) => tag.type === 'pinned')
  const attached = tags.filter((tag) => tag.type !== 'pinned')
  /* The CAP is the server's, and it counts `type === 'pinned'` only. */
  const pinnedCount = pinned.length
  const atCapacity = pinnedCount >= MAX_PINNED_SENSORS
  /* Records from before `is_source` shipped arrive with no tags at all; the
     analysis still names the sensors, one symptom row each. */
  const symptomNames = symptomSensorNames(anomaly?.content)

  /**
   * Write a new target set.
   *
   * ── Built from `tags`, every time, because the body is the whole set ────
   * `PUT .../sensor-tags` soft-deletes anything the body leaves out. So every
   * caller below starts from what is on screen and changes one thing in it;
   * none of them sends just the tag it is touching, which would delete the rest
   * and return 200.
   */
  const write = (next) =>
    save.mutate({
      anomalyId: anomaly.id,
      tags: next.map((tag) => ({ sensorTagId: tag.sensorTagId, type: tag.type })),
    })

  const setType = (sensorTagId, type) =>
    write(tags.map((tag) => (tag.sensorTagId === sensorTagId ? { ...tag, type } : tag)))

  /**
   * Removal takes the tag OFF the anomaly — the row goes.
   *
   * Offered from the added list only, never from a pinned card. Unpinning is the
   * reversible move and it is what a crowded board actually calls for; putting
   * an irreversible one beside it, as two matching icon buttons, made the wrong
   * click cheap. A sensor is now unpinned first and removed second, from a list
   * row rather than from a chart somebody is reading.
   */
  const remove = (sensorTagId) => write(tags.filter((tag) => tag.sensorTagId !== sensorTagId))

  /* Added `unpinned`. Attaching and charting are two steps now: twenty sensors
     on one anomaly is a reasonable set to have gathered and an unreadable set to
     have drawn, so adding one does not put a chart on the screen. */
  const add = (sensorTagIds) =>
    write([
      ...tags,
      ...sensorTagIds.map((sensorTagId) => ({ sensorTagId, type: 'unpinned' })),
    ])

  const expandedTag = pinned.find((tag) => tag.sensorTagId === expanded) ?? null

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
        <Eyebrow as="h2">
          {t('anomalies.analysis.ts.pinned', { count: pinnedCount, max: MAX_PINNED_SENSORS })}
        </Eyebrow>

        <div className="flex-1" />

        {/* The prototype's "Anomaly history / Back to live" pair, as a segmented
            control. Two named states rather than one button that renames itself:
            a button labelled "Back to live" has to be read to find out which mode
            you are currently in, which is the one thing it should be telling you
            at a glance. */}
        <Segmented label={t('anomalies.analysis.ts.modeLabel')}>
          {/* Widened off the icon-sized default: these two carry words, because
              an icon pair would have to teach that a calendar means "the event"
              and a waveform means "now" — which is the distinction the control
              exists to make plain. */}
          <SegmentedItem
            selected={mode === 'history'}
            label={t('anomalies.analysis.ts.modeHistory')}
            onClick={() => setMode('history')}
            className="w-auto px-3 text-body-sm font-medium"
          >
            {t('anomalies.analysis.ts.modeHistory')}
          </SegmentedItem>
          <SegmentedItem
            selected={mode === 'live'}
            label={t('anomalies.analysis.ts.modeLive')}
            onClick={() => setMode('live')}
            className="w-auto px-3 text-body-sm font-medium"
          >
            {t('anomalies.analysis.ts.modeLive')}
          </SegmentedItem>
        </Segmented>

        {/* ADD, not pin. The button attaches a sensor to the anomaly; charting it
            is the pin control on its row. Always shown to anybody who can reach
            this screen, and the dialog says what is missing if the permissions
            are not there — hiding it would make a role boundary look like a
            product that cannot do the thing at all. */}
        <ForgeButton size="sm" disabled={save.isPending} onClick={() => setAdding(true)}>
          <Icon name="plus" size="xs" />
          {t('anomalies.analysis.ts.addSensor')}
        </ForgeButton>
      </div>

      {/* The four stamps that describe the event, on their own line. They were a
          bare range beside the heading; four facts would have crowded the
          segmented control and the Add button off the row at laptop width.

          History only. In live mode none of this is what is on screen. */}
      {mode === 'history' ? <EventStamps anomaly={anomaly} /> : null}

      {/* One error line for the whole board rather than one per card: a failed
          write is a failed write of the SET, and it leaves every card as it was.

          ── The location refusal is named, because it is not the user's fault ──
          `verify_linked_sensor_tags` 400s with `fields.sensor_tag_id` when an
          added sensor is not at the anomaly's exact level. On a plant- or
          line-level anomaly that is EVERY sensor the picker offered, so the
          generic "some values were rejected" would read as the reader having
          picked wrongly from a list that gave them no other option. */}
      {save.isError ? (
        <ErrorState
          error={save.error}
          title={
            isSourceRefusal(save.error)
              ? t('anomalies.analysis.ts.sourceRefused')
              : isLocationRefusal(save.error)
                ? t('anomalies.analysis.ts.outOfScope')
                : t('anomalies.analysis.ts.saveFailed')
          }
        />
      ) : null}

      {!pinned.length ? (
        <Panel className="flex flex-col gap-1 py-10 text-center">
          <p className="m-0 text-body font-semibold text-ink">
            {t(tags.length ? 'anomalies.analysis.ts.nonePinnedTitle' : 'anomalies.analysis.ts.empty')}
          </p>
          <p className="m-0 text-body-sm text-ink-dim">
            {t(tags.length ? 'anomalies.analysis.ts.nonePinned' : 'anomalies.analysis.ts.emptyBody')}
          </p>
          {!tags.length && symptomNames.length ? (
            <p className="m-0 mt-2 text-body-sm text-ink">
              {t('anomalies.analysis.ts.alertSensors', { names: symptomNames.join(', ') })}
            </p>
          ) : null}
        </Panel>
      ) : mode === 'live' ? (
        <LivePanel
          anomaly={anomaly}
          tags={pinned}
          busy={save.isPending}
          onUnpin={(id) => setType(id, 'unpinned')}
        />
      ) : (
        <div className="grid gap-3 [grid-template-columns:repeat(auto-fit,minmax(min(100%,26rem),1fr))]">
          {pinned.map((tag) => (
            <HistoryCard
              key={tag.sensorTagId}
              anomaly={anomaly}
              tag={tag}
              busy={save.isPending}
              onUnpin={() => setType(tag.sensorTagId, 'unpinned')}
              onExpand={() => setExpanded(tag.sensorTagId)}
            />
          ))}
        </div>
      )}

      <AttachedList
        tags={attached}
        busy={save.isPending}
        atCapacity={atCapacity}
        onPin={(sensorTagId) => setType(sensorTagId, 'pinned')}
        onRemove={remove}
      />

      {/* History only. A live card renders its own dialog from its own stream —
          see `LiveCard`. */}
      <FullscreenChart anomaly={anomaly} tag={expandedTag} onClose={() => setExpanded(null)} />

      <AddSensorDialog
        open={adding}
        anomaly={anomaly}
        attachedIds={tags.map((tag) => tag.sensorTagId)}
        pending={save.isPending}
        onClose={() => setAdding(false)}
        onConfirm={(ids) => {
          add(ids)
          setAdding(false)
        }}
      />
    </div>
  )
}

/**
 * When the event happened, as the four stamps `AnomalyOut` actually carries.
 *
 * ── Why four and not the two that were here ────────────────────────────
 * The header showed `window_start — window_end`, which is the range the traces
 * are drawn over and is only half the record. `detected_at` is when the detector
 * fired, and `last_detected_at` is the last time it fired — and on an extending
 * anomaly that one lands AFTER the window closes. On ANM-288 the window ends at
 * 12:05:17 and the last detection is at 12:05:23, six seconds later: the chart
 * genuinely cannot show the moment the record was last touched by the detector,
 * and a header claiming the window was the whole story hid that.
 *
 * ── The date once, the times beside it ─────────────────────────────────
 * All four almost always fall inside one minute, so four full stamps would be
 * four repetitions of the one part that does not vary — and would wrap to three
 * lines on a laptop. The date leads; the times follow with SECONDS, because at
 * minute precision these four print the same value and the row reads as broken.
 *
 * The fallback is real and not theoretical: an anomaly whose window crosses
 * midnight gets full stamps per fact instead, because "06:56" under a date of
 * the 8th is simply wrong when the reading was taken on the 9th.
 *
 * Absent values print an em dash rather than being dropped. A missing
 * `last_detected_at` is a fact about the record; a fact that silently vanishes
 * from a row of four makes the reader count them.
 */
function EventStamps({ anomaly }) {
  const { t } = useTranslation(['app', 'common'])

  const { windowStart, windowEnd, detectedAt, lastDetectedAt } = anomaly ?? {}
  const stamps = [windowStart, windowEnd, detectedAt, lastDetectedAt]
  if (!stamps.some(Boolean)) return null

  /* One day for all four, or none. Anchored on `windowStart` because it is the
     earliest of them by construction and is the one the date label names. */
  const oneDay =
    Boolean(windowStart) && stamps.filter(Boolean).every((value) => sameDayInZone(windowStart, value))

  const show = (value) => {
    if (!value) return '—'
    return oneDay ? formatDateTime(value, IST_TIME) : formatDateTime(value, IST_STAMP)
  }

  return (
    <div className="flex flex-wrap items-baseline gap-x-4 gap-y-1.5">
      {oneDay ? (
        <Num className="text-body-sm font-medium text-ink">
          {formatDateTime(windowStart, IST_DATE)}
        </Num>
      ) : null}

      <Fact label={t('anomalies.analysis.ts.fact.window')}>
        {t('anomalies.analysis.ts.window', { from: show(windowStart), to: show(windowEnd) })}
      </Fact>
      <Fact label={t('anomalies.analysis.ts.fact.detected')}>{show(detectedAt)}</Fact>
      <Fact label={t('anomalies.analysis.ts.fact.lastDetected')}>{show(lastDetectedAt)}</Fact>
    </div>
  )
}

/** A 10px uppercase label and its value, on one baseline. */
function Fact({ label, children }) {
  return (
    <span className="flex items-baseline gap-1.5">
      <span className="text-micro font-semibold tracking-wide text-ink-mute uppercase">
        {label}
      </span>
      <Num className="text-body-sm text-ink-dim">{children}</Num>
    </span>
  )
}

/**
 * Whether a failed write is the server refusing a sensor's LOCATION.
 *
 * Matched on the field and the status rather than on the sentence: `sensor_tag_id`
 * is the structured half of the `ValidationAppError` that `verify_owned_ids`
 * raises, and the prose beside it is the part a backend may reword.
 *
 * This becomes dead the day `verify_sensor_tags` passes `beneath=True`, which is
 * the fix — at which point the branch simply stops being taken.
 */
const isLocationRefusal = (error) =>
  error?.status === 400 && typeof error?.fields?.sensor_tag_id === 'string' && !isSourceRefusal(error)

/**
 * The write left out a tag the ENGINE attached, and nothing was saved.
 *
 * This screen never offers to remove a source tag, so the only way here is a
 * stale body: the engine extended the alert after this page read it, and the
 * write was built from the older set. The hook refetches on error, so the next
 * attempt is built from the current one. Matched on the server's sentence —
 * `verify_sensor_tags` uses the same field key as the location refusal, and the
 * text is the only thing that tells the two apart.
 */
const isSourceRefusal = (error) =>
  error?.status === 400 && error?.fields?.sensor_tag_id === "Source tags can't be removed."

/**
 * The sensors attached to this anomaly that are not charted.
 *
 * ── A list, not a grid of empty cards ──────────────────────────────────
 * These have no trace by definition, so they take a row each. Twenty charts is
 * the ceiling precisely because a chart is expensive to read; giving the other
 * forty a card-shaped placeholder would spend the same page area saying nothing.
 *
 * ── Two kinds of row sit here and the screen does not pretend otherwise ──
 * The engine unpins what it considered and set aside, and a person adds a sensor
 * here before charting it. Both are "attached, not charted", which is one state
 * and is what this list is named for. Nothing on the wire distinguishes who
 * wrote the row — `AnomalySensorTagOut` carries no actor — so the screen does
 * not claim to know.
 */
function AttachedList({ tags, busy, atCapacity, onPin, onRemove }) {
  const { t } = useTranslation(['app', 'common'])
  if (!tags.length) return null

  return (
    <Panel flush>
      <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1 border-b border-hairline px-4 py-3">
        <Eyebrow as="h3">{t('anomalies.analysis.ts.attached', { count: tags.length })}</Eyebrow>
        <p className="m-0 text-body-sm text-ink-dim">{t('anomalies.analysis.ts.attachedHint')}</p>
      </div>

      <ul className="m-0 list-none p-0">
        {tags.map((tag) => (
          <li
            key={tag.sensorTagId}
            className="flex flex-wrap items-center gap-x-3 gap-y-1.5 border-b border-hairline px-4 py-2.5 last:border-0"
          >
            <span className="min-w-0 flex-1">
              <span className="block truncate text-body-sm font-semibold text-ink">
                {tag.name || t('anomalies.analysis.ts.unnamed')}
              </span>
              <Mono className="mt-0.5 block text-micro text-ink-mute">
                {t(`dataFusion.sensors.valueType.${tag.valueType}`, { defaultValue: tag.valueType })}
              </Mono>
            </span>

            {tag.connectionPaused ? (
              <StateBadge tone="amber" dot={false}>
                {t('anomalies.analysis.ts.paused')}
              </StateBadge>
            ) : null}

            {tag.isSource ? <PlainBadge>{t('anomalies.analysis.ts.source')}</PlainBadge> : null}

            {/* Disabled at the ceiling rather than hidden, with the reason on the
                control itself — a Pin button that silently stops working is the
                failure this exists to avoid. */}
            <ForgeButton
              size="sm"
              disabled={busy || atCapacity}
              title={atCapacity ? t('anomalies.analysis.ts.pinFull', { max: MAX_PINNED_SENSORS }) : undefined}
              onClick={() => onPin(tag.sensorTagId)}
            >
              <Icon name="pin" size="xs" />
              {t('anomalies.analysis.ts.pin')}
            </ForgeButton>

            {/* No remove on a source tag: the server refuses it, and a button
                that always 400s is worse than none. */}
            {tag.isSource ? null : (
              <IconButton
                bare
                disabled={busy}
                label={t('anomalies.analysis.ts.remove', {
                  name: tag.name || t('anomalies.analysis.ts.unnamed'),
                })}
                onClick={() => onRemove(tag.sensorTagId)}
              >
                <Icon name="x" size="sm" />
              </IconButton>
            )}
          </li>
        ))}
      </ul>

      {atCapacity ? (
        <p className="m-0 border-t border-hairline px-4 py-2.5 text-body-sm text-ink-mute">
          {t('anomalies.analysis.ts.pinFull', { max: MAX_PINNED_SENSORS })}
        </p>
      ) : null}
    </Panel>
  )
}

/**
 * The zoom chips this window can honestly offer.
 *
 * Only spans SHORTER than the window, plus the window itself. A 75-second event
 * offered a 5-minute chip would either show four minutes of blank axis or
 * silently clamp — and a control that silently does nothing is worse than one
 * that is absent, because the reader concludes the data is missing rather than
 * the control.
 */
function spansFor(totalMs) {
  if (!Number.isFinite(totalMs) || totalMs <= 0) return []
  const fits = HISTORY_SPANS.filter((span) => span.ms < totalMs)
  return fits.length ? [{ key: 'full', ms: null }, ...fits] : []
}

/**
 * The detector's own interval, for the band drawn over every trace.
 *
 * `detected_at` to `last_detected_at` — when the detection FIRED and when it
 * last fired, which is a different interval from `window_start`/`window_end` and
 * is the one the chart could not previously show. On an extending anomaly the
 * second routinely falls outside the window the readings cover.
 *
 * Null unless both ends are present: half a band is a line with no stated
 * meaning, and the reader would have to guess which end it was.
 */
function detectionBand(anomaly) {
  if (!anomaly?.detectedAt || !anomaly?.lastDetectedAt) return null
  return { from: anomaly.detectedAt, to: anomaly.lastDetectedAt }
}

/** The window length in ms, or null when the record does not carry both ends. */
function windowLength(anomaly) {
  if (!anomaly?.windowStart || !anomaly?.windowEnd) return null
  const span = Date.parse(anomaly.windowEnd) - Date.parse(anomaly.windowStart)
  return Number.isFinite(span) && span > 0 ? span : null
}

/**
 * One pinned tag over the anomaly window.
 *
 * ── Each card reads on its own ───────────────────────────────────────────
 * `authorize_sensor_tag_view` runs per tag server-side, and a tag can 403 or 404
 * while its neighbours resolve. One query per card means that failure stays on
 * the card it belongs to instead of emptying the board — and the reader is told
 * which sensor they cannot see, which is the part that matters.
 */
function HistoryCard({ anomaly, tag, busy, onUnpin, onExpand }) {
  const { t } = useTranslation(['app', 'common'])
  const [spanMs, setSpanMs] = useState(null)
  const { toast } = useToast()
  const [exporting, setExporting] = useState(false)
  const { readings, isPending, isError, error, refetch, hasMore, loadingMore, loadMore, loadAll } =
    useSensorTagReadings(anomaly.id, tag.sensorTagId)
  const spans = useMemo(() => spansFor(windowLength(anomaly)), [anomaly])
  const band = detectionBand(anomaly)

  /* The whole anomaly window, not the part panned to — see `loadAll`. Stamps
     are the server's UTC instants, unformatted, so a spreadsheet sorts them. */
  const exportCsv = async () => {
    setExporting(true)
    try {
      const all = await loadAll()
      const name = tag.name || t('anomalies.analysis.ts.unnamed')
      downloadCsv(
        `${anomaly.serialNumber ?? anomaly.id}-${name}.csv`.replace(/[^\w.-]+/g, '_'),
        toCsv(['timestamp', name], all.map((reading) => [reading.at ?? '', reading.value])),
      )
    } catch {
      toast(t('anomalies.analysis.ts.exportFailed'), 'error')
    } finally {
      setExporting(false)
    }
  }

  return (
    <Panel flush className="flex flex-col">
      <SensorHeader
        tag={tag}
        busy={busy}
        onUnpin={onUnpin}
        action={
          readings.length ? (
            <ForgeButton size="sm" onClick={exportCsv} disabled={exporting}>
              <Icon name="doc" size="xs" />
              {t('anomalies.analysis.ts.exportCsv')}
            </ForgeButton>
          ) : null
        }
      />

      <div className="p-4">
        {isPending ? (
          /* `spans.length` so the chip row below it is the width it will be, and
             not a guess that shifts when the real chips arrive. */
          <TraceSkeleton spans={spans.length} />
        ) : isError ? (
          <ErrorState
            error={error}
            title={t('anomalies.analysis.ts.failed')}
            onRetry={() => refetch()}
          />
        ) : readings.length ? (
          <>
            <TraceChart
              readings={readings}
              spans={spans}
              spanMs={spanMs}
              onSpanChange={setSpanMs}
              band={band}
              hasMore={hasMore}
              loadingMore={loadingMore}
              onLoadMore={loadMore}
              onFullscreen={onExpand}
            />
          </>
        ) : (
          <p className="m-0 py-6 text-center text-body-sm text-ink-dim">
            {t('anomalies.analysis.ts.noReadings')}
          </p>
        )}
      </div>
    </Panel>
  )
}

/**
 * Live — every pinned sensor, streaming.
 *
 * ── A grid, matching history, and the connection cost is real ───────────
 * One card per pinned tag, because the two modes show the same board and a mode
 * that silently narrowed it to one sensor would make "pinned" mean two different
 * things. The cost is one SSE connection per card: `stream_anomaly_sensor_tag`
 * opens a connection for a SINGLE tag, and `try_acquire(user.id,
 * "anomaly_sensor_tags", max_concurrent_streams_per_user)` caps them at four by
 * default. A board of twenty therefore needs that setting raised, or — better —
 * a route that takes several tag ids on one connection, which the push layer's
 * `event_stream(user_id, purpose, sensor_tag_ids)` already accepts and the sensor
 * chart already uses. It is in BACKEND-GAPS.md.
 *
 * Nothing is hidden while that stands: a refused connection is a failed one, and
 * the card says so in its own status badge rather than sitting blank.
 *
 * ── A live trace is NOT the anomaly ────────────────────────────────────
 * It is what the sensor is reporting now, which for a record raised last week has
 * nothing to do with the window the analysis was written from. The note under
 * each chart says so, because a trace with no label beside it in a screen titled
 * with an anomaly's serial number will be read as that anomaly's.
 */
function LivePanel({ anomaly, tags, busy, onUnpin }) {
  return (
    <div className="grid gap-3 [grid-template-columns:repeat(auto-fit,minmax(min(100%,26rem),1fr))]">
      {tags.map((tag) => (
        <LiveCard
          key={tag.sensorTagId}
          anomaly={anomaly}
          tag={tag}
          busy={busy}
          onUnpin={() => onUnpin(tag.sensorTagId)}
        />
      ))}
    </div>
  )
}

/**
 * One pinned tag, live.
 *
 * ── The card owns its stream, and expanding does not touch it ───────────
 * The expanded view is rendered from THIS component's readings, which is why the
 * dialog lives here rather than beside the history one at the top of the screen.
 *
 * It used to be shared with that dialog, and the sequence was: expand, the card
 * disables its stream, the dialog opens a second one for the same tag; collapse,
 * and the pair swap back. Three things went wrong with it.
 *
 * The dialog's buffer started EMPTY, so a chart that had been live for a minute
 * opened on "Waiting for readings…" and had to refill from the server's 60s
 * backfill.
 *
 * Every expand and collapse churned a connection, and a slot is not freed the
 * instant a client aborts — the generator is parked on `await queue.get()` until
 * the next reading or the next write attempt, so on a quiet sensor the release
 * is late. A few cycles and `try_acquire` is at its ceiling of four.
 *
 * And the refusal is a 400 (`ValidationAppError`), which `services/sse.js` used
 * to treat as retryable — so the card came back from a collapse and sat on
 * "Connecting…" forever. That is fixed there as well, but the fix that matters
 * is this one: there is now exactly one connection per pinned tag for as long as
 * the card is mounted, and expanding it costs nothing.
 */
function LiveCard({ anomaly, tag, busy, onUnpin }) {
  const { t } = useTranslation(['app', 'common'])
  const [spanMs, setSpanMs] = useState(DEFAULT_LIVE_SPAN)
  const [fullscreen, setFullscreen] = useState(false)
  const { readings, status } = useLiveSensorTagReadings(anomaly.id, tag.sensorTagId, {
    keepMs: spanMs,
  })

  const name = tag.name || t('anomalies.analysis.ts.unnamed')
  /* Passed in live too, and it will almost never draw: the detection is history
     and the feed is now. On an anomaly still firing the two DO overlap, and that
     is exactly the case worth seeing — so the decision is left to the range test
     in `TraceChart` rather than made here by assuming. */
  const band = detectionBand(anomaly)

  /* One element, rendered in whichever of the two places is showing. The
     readings, the span and the pan state all come from here, so the expanded
     view is the same chart rather than a second one that has to catch up. */
  const chart = (
    <TraceChart
      readings={readings}
      spans={LIVE_SPANS}
      spanMs={spanMs}
      onSpanChange={setSpanMs}
      band={band}
      live
      fullscreen={fullscreen}
      onFullscreen={() => setFullscreen((value) => !value)}
    />
  )

  return (
    <Panel flush className="flex flex-col">
      <SensorHeader
        tag={tag}
        busy={busy}
        onUnpin={onUnpin}
        /* The connection, named. A live chart that has gone quiet because its
           socket dropped looks exactly like a sensor that has gone quiet, and
           those are opposite problems — which matters most at the cap, where the
           refusal is the server's and not the sensor's. */
        badge={
          <StateBadge
            tone={status === 'live' ? 'green' : status === 'connecting' ? 'amber' : 'red'}
          >
            {t(`anomalies.analysis.ts.stream.${status}`, {
              defaultValue: t('anomalies.analysis.ts.stream.idle'),
            })}
          </StateBadge>
        }
      />

      {/* One of the two renders at a time. `chart` is a single element and both
          slots would mount their own copy of it — same readings, but two charts
          doing the same work, and the one behind the dialog invisible. The cost
          of swapping is the pan offset, which lives in `TraceChart` and resets on
          expand; the stream does not, which is the part that matters. */}
      <div className="p-4">{fullscreen ? null : chart}</div>

      {/* `full`, not a centred card: the expanded chart takes the viewport, and
          the plot absorbs whatever the controls and figures leave, so the whole
          thing is read in one screen with nothing to scroll. */}
      <ForgeDialog
        open={fullscreen}
        onOpenChange={(next) => (next ? null : setFullscreen(false))}
        size="full"
        title={name}
        description={t('anomalies.analysis.ts.liveNote')}
      >
        {fullscreen ? chart : null}
      </ForgeDialog>
    </Panel>
  )
}

/**
 * One history trace, bigger.
 *
 * A dialog rather than the Fullscreen API: the chart has to keep the app's theme
 * tokens, its own controls and a close affordance people already recognise, and
 * `requestFullscreen` on an element inside a scroll container is the kind of
 * thing that works until somebody opens it on a second monitor.
 *
 * ── History only, and live does not come through here ──────────────────
 * It used to take a `mode` and open a live stream when expanded from the live
 * board. That second stream was the bug: it started with an empty buffer and it
 * churned a connection on every expand and collapse. A live card renders its own
 * dialog off its own readings instead — see `LiveCard`.
 *
 * This one costs nothing: `useSensorTagReadings` is keyed by the same pair and
 * held at `staleTime: Infinity`, so the expanded view opens on exactly the pages
 * the card had already fetched — and a page loaded from here stays loaded on the
 * card behind it.
 */
function FullscreenChart({ anomaly, tag, onClose }) {
  const { t } = useTranslation(['app', 'common'])
  const [spanMs, setSpanMs] = useState(null)
  const { readings, hasMore, loadingMore, loadMore } = useSensorTagReadings(
    anomaly?.id,
    tag?.sensorTagId,
    { enabled: Boolean(tag) },
  )

  const spans = useMemo(() => spansFor(windowLength(anomaly)), [anomaly])

  return (
    <ForgeDialog
      open={Boolean(tag)}
      onOpenChange={(next) => (next ? null : onClose())}
      size="full"
      title={tag?.name || t('anomalies.analysis.ts.unnamed')}
      description={t('anomalies.analysis.ts.window', {
        from: anomaly?.windowStart ? formatDateTime(anomaly.windowStart, IST_STAMP) : '—',
        to: anomaly?.windowEnd ? formatDateTime(anomaly.windowEnd, IST_STAMP) : '—',
      })}
    >
      <TraceChart
        readings={readings}
        spans={spans}
        spanMs={spanMs}
        onSpanChange={setSpanMs}
        band={detectionBand(anomaly)}
        hasMore={hasMore}
        loadingMore={loadingMore}
        onLoadMore={loadMore}
        fullscreen
      />
    </ForgeDialog>
  )
}

/**
 * The name, the type, the paused flag, and the one control a pinned card offers.
 *
 * ── Unpin only. Remove is not reachable from the board ─────────────────
 * A pinned card had both, and the pair was a trap: two small icon buttons side
 * by side, one reversible and one not, on the control somebody reaches for when
 * they have too many charts on screen. The frequent intent there is "stop
 * charting this", and the destructive option sat one target away from it.
 *
 * So the board unpins, and the sensor drops to the added list — where Remove
 * lives, next to Pin, on a row that is a list entry rather than a chart somebody
 * is reading. Taking a sensor off the anomaly is now deliberately two steps, and
 * the first of them is free to get wrong.
 */
function SensorHeader({ tag, busy, onUnpin, badge = null, action = null }) {
  const { t } = useTranslation(['app', 'common'])

  return (
    <div className="flex flex-wrap items-center gap-x-2 gap-y-1 border-b border-hairline px-4 py-3">
      <span className="min-w-0 flex-1">
        <span className="block truncate text-body-sm font-semibold text-ink">
          {tag.name || t('anomalies.analysis.ts.unnamed')}
        </span>
        <Mono className="mt-0.5 block text-micro text-ink-mute">
          {t(`dataFusion.sensors.valueType.${tag.valueType}`, { defaultValue: tag.valueType })}
        </Mono>
      </span>

      {/* The CONNECTION's state, not the tag's. A paused connection is why a
          trace can be flat or absent without anything being wrong with the
          sensor, and that distinction is unreadable off the chart alone. */}
      {badge}

      {action}

      {tag.connectionPaused ? (
        <StateBadge tone="amber" dot={false}>
          {t('anomalies.analysis.ts.paused')}
        </StateBadge>
      ) : null}

      {/* One of the sensors that raised the alert. Unpinnable like the rest;
          the badge says why it is here. */}
      {tag.isSource ? <PlainBadge>{t('anomalies.analysis.ts.source')}</PlainBadge> : null}

      {/* Named for the SENSOR, not the verb alone: several of these sit side
          by side, and an icon-only control that reads the same on every card
          is not a name. */}
      <IconButton
        bare
        disabled={busy}
        label={t('anomalies.analysis.ts.unpinOne', {
          name: tag.name || t('anomalies.analysis.ts.unnamed'),
        })}
        title={t('anomalies.analysis.ts.unpinOne', {
          name: tag.name || t('anomalies.analysis.ts.unnamed'),
        })}
        onClick={onUnpin}
      >
        <Icon name="pin-off" size="sm" />
      </IconButton>
    </div>
  )
}

/**
 * Pick sensor tags to pin.
 *
 * ── Two permissions, from two different modules, and only one is ours ────
 * The write needs `anomalies.manage` (the route) plus `sensor_explorer.manage`
 * or `sensor_explorer.use` (`verify_sensor_tags`, for tags new to the set). The
 * LIST needs `sensors.manage_sensors` or `sensors.manage_connections` — a third
 * module, with no bearing on whether somebody may pin.
 *
 * So a triage role can be allowed to pin and not allowed to browse for something
 * to pin. That is a real gap and it is in BACKEND-GAPS.md; what this dialog does
 * meanwhile is say which half is missing, rather than opening on an empty list or
 * on a 403 from a request nobody expected to make.
 *
 * ── The list is the anomaly's EXACT level ──────────────────────────────
 * `GET /v1/sensors` filtered by whichever of `plant_id`/`line_id`/`machine_id`
 * the anomaly is attached at — a single-column comparison, not a subtree. That
 * is the same rule `verify_sensor_tags` applies when the tag is linked, so every
 * row offered here can actually be added.
 *
 * It listed the SUBTREE for a while (`asset_type`/`asset_id`), which is the
 * scope the fault can be in — an ingestion outage is filed at the plant precisely
 * because the silent sensors are spread across its machines. The server does not
 * accept that: `verify_linked_sensor_tags` is called without `beneath=True`, so
 * every one of those rows was a 400 on save. Matching the endpoint is the honest
 * arrangement; the gap is real and is in BACKEND-GAPS.md, where a one-keyword fix
 * reopens the wider list.
 */
function AddSensorDialog({ open, anomaly, attachedIds, pending, onClose, onConfirm }) {
  const { t } = useTranslation(['app', 'common'])
  const [selected, setSelected] = useState([])

  /* Either grant lets a tag be linked; `verify_sensor_tags` asks for the pair
     with an OR. The browse permissions are likewise either-or.

     Each hook is called on its own line and the OR is applied after, because
     `a().granted || b().granted` short-circuits — which would skip a hook call on
     the renders where the first one is true, and hooks may not be conditional. */
  const linkManage = usePermission('sensor_explorer.manage')
  const linkUse = usePermission('sensor_explorer.use')
  const browseSensors = usePermission('sensors.manage_sensors')
  const browseConnections = usePermission('sensors.manage_connections')

  const canLink = linkManage.granted || linkUse.granted
  const canBrowse = browseSensors.granted || browseConnections.granted

  const scope = anomalyAssetScope(anomaly)
  /* `...scope.filter` is one of `plantId` / `lineId` / `machineId` — the EXACT
     level, which is the only set the link check will accept. See
     `anomalyAssetScope`. */
  const { data, isPending, isError, error, refetch } = useSensors({
    ...(scope?.filter ?? {}),
    page: 1,
    pageSize: 100,
    enabled: open && canBrowse && Boolean(scope),
  })

  /* Flattened to TAGS, which is what the endpoint takes — a sensor is the thing
     that carries a location, a tag is the thing that carries readings. */
  const rows = useMemo(() => {
    const already = new Set(attachedIds)
    return (data?.items ?? []).flatMap((sensor) =>
      (sensor.tags ?? []).map((tag) => ({
        id: tag.id,
        name: tag.name,
        valueType: tag.valueType,
        sensorName: sensor.name,
        /* Already ON the anomaly, pinned or not. Both are "do not offer this
           again" — adding a tag that is already in the set is a no-op the server
           would accept and the reader would read as a second copy. */
        attached: already.has(tag.id),
      })),
    )
  }, [data, attachedIds])

  const close = () => {
    setSelected([])
    onClose()
  }

  const toggle = (id) =>
    setSelected((all) => (all.includes(id) ? all.filter((entry) => entry !== id) : [...all, id]))

  return (
    <ForgeDialog
      open={open}
      onOpenChange={(next) => (next ? null : close())}
      title={t('anomalies.analysis.ts.addTitle')}
      description={
        scope
          ? t(`anomalies.analysis.ts.addBody.${scope.level}`, {
              asset:
                anomaly.hierarchy?.machine ??
                anomaly.hierarchy?.line ??
                anomaly.hierarchy?.plant ??
                t('anomalies.analysis.ts.thisAsset'),
            })
          : t('anomalies.analysis.ts.addNoAsset')
      }
      footer={
        <>
          <ForgeButton onClick={close}>{t('common:actions.cancel')}</ForgeButton>
          <ForgeButton
            variant="primary"
            disabled={!selected.length || pending || !canLink}
            /* Cleared on the way out, not on the way in: the dialog stays
               mounted, so a selection left behind here would be pre-ticked the
               next time somebody opened it — against tags that are by then
               already pinned. */
            onClick={() => {
              onConfirm(selected)
              setSelected([])
            }}
          >
            {t('anomalies.analysis.ts.addConfirm', { count: selected.length })}
          </ForgeButton>
        </>
      }
    >
      {!canLink ? (
        <EmptyState
          icon="lock"
          title={t('anomalies.analysis.ts.noLinkPermission')}
          body={t('anomalies.analysis.ts.noLinkPermissionBody')}
          className="border-0 shadow-none"
        />
      ) : !canBrowse ? (
        <EmptyState
          icon="lock"
          title={t('anomalies.analysis.ts.noBrowsePermission')}
          body={t('anomalies.analysis.ts.noBrowsePermissionBody')}
          className="border-0 shadow-none"
        />
      ) : !scope ? (
        <EmptyState
          icon="bolt"
          title={t('anomalies.analysis.ts.addNoAsset')}
          body={t('anomalies.analysis.ts.addNoAssetBody')}
          className="border-0 shadow-none"
        />
      ) : isPending ? (
        <SkeletonRows count={5} />
      ) : isError ? (
        <ErrorState
          error={error}
          title={t('anomalies.analysis.ts.browseFailed')}
          onRetry={() => refetch()}
        />
      ) : !rows.length ? (
        /* Not "nothing found" — "nothing AT THIS LEVEL". On a plant-level
           anomaly this is the ordinary case, and a bare empty state would read
           as a broken list rather than as the rule it is. */
        <EmptyState
          icon="gauge"
          title={t('anomalies.analysis.ts.noCandidates')}
          body={t(`anomalies.analysis.ts.noCandidatesBody.${scope.level}`, {
            asset:
              anomaly.hierarchy?.machine ??
              anomaly.hierarchy?.line ??
              anomaly.hierarchy?.plant ??
              t('anomalies.analysis.ts.thisAsset'),
          })}
          className="border-0 shadow-none"
        />
      ) : (
        <ul className="m-0 flex max-h-[22rem] list-none flex-col gap-1 overflow-y-auto p-0">
          {rows.map((row) => {
            const disabled = row.attached
            const on = selected.includes(row.id)
            return (
              <li key={row.id}>
                <button
                  type="button"
                  disabled={disabled}
                  aria-pressed={on}
                  onClick={() => toggle(row.id)}
                  className={cn(
                    'flex w-full items-center gap-3 rounded-sm border px-3 py-2.5 text-start transition-colors',
                    on ? 'border-forge bg-sunken' : 'border-hairline bg-card',
                    disabled ? 'opacity-55' : 'hover:border-forge',
                  )}
                >
                  <Icon
                    name={on ? 'check' : 'gauge'}
                    size="sm"
                    className={cn('shrink-0', on ? 'text-forge' : 'text-ink-mute')}
                  />
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-body-sm font-semibold text-ink">
                      {row.name || t('anomalies.analysis.ts.unnamed')}
                    </span>
                    <span className="mt-0.5 block truncate text-micro font-normal text-ink-dim">
                      {row.sensorName}
                      {' · '}
                      {t(`dataFusion.sensors.valueType.${row.valueType}`, {
                        defaultValue: row.valueType,
                      })}
                    </span>
                  </span>

                  {row.attached ? (
                    <StateBadge tone="ack" dot={false}>
                      {t('anomalies.analysis.ts.alreadyAdded')}
                    </StateBadge>
                  ) : null}
                </button>
              </li>
            )
          })}
        </ul>
      )}
    </ForgeDialog>
  )
}
